import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext } from '@eforall/dashboard';
import { VolunteerDetails, VolunteerStatusId } from '@interfaces';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { LeadVolunteerInterviewDialogPart } from './interview/lead-volunteer-interview-dialog.part';
import { VolunteerManagementDialogPart } from './management/management-dialog.part';
import { VolunteerMentorSignupsDialogPart } from './mentor-sign-ups/mentor-signups-dialog.part';
import { OverviewContentDialogPart } from './overview-content/overview-content-dialog.part';
import { VolunteerSchedulePart } from './schedule/schedule.part';
import { VolunteerSpecialistSignupsDialogPart } from './specialist-sign-ups/specialist-signups-dialog.part';
import { VolunteerStatusLogDialogPart } from './status-log/status-log-dialog.part';
import { VolunteerDialogTopicsPart } from './topics/topics.part';
import { VolunteerDialogDataService } from './volunteer-dialog-data.service';

@Component({
	selector: 'volunteer-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		TabStripModule,
		VolunteerStatusLogDialogPart,
		OverviewContentDialogPart,
		LeadVolunteerInterviewDialogPart,
		VolunteerDialogTopicsPart,
		VolunteerMentorSignupsDialogPart,
		VolunteerSchedulePart,
		VolunteerSpecialistSignupsDialogPart,
		VolunteerManagementDialogPart
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './volunteer.dialog.html'
})

export class VolunteerDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<VolunteerDialog>);
	public dialogContext: DialogContext<{ volunteerId: number }> = inject(MAT_DIALOG_DATA);

	private dialogDataService = inject(VolunteerDialogDataService);

	public readonly headerName = computed(() => {
		const volunteer = this.dialogDataService.volunteerDetails();
		if (!volunteer) return 'Application';

		const prefix = volunteer.volunteerStatusId === VolunteerStatusId.Pending ? 'Lead Volunteer' : 'Volunteer';

		return `${prefix}: ${volunteer.person.fullName}`;
	});

	public readonly canSeeManagementTab = computed(() => {
		return this.dialogDataService.canManageVolunteerStaff() || this.dialogDataService.app.data().permissions.isSoftwareDev;
	});

	ngOnInit() {
		const data = this.dialogContext.data;

		if (data)
			this.dialogDataService.getVolunteerDetails(data.volunteerId);
	}

	public actions: DialogAction[] = [{
		id: 'close',
		cssClass: signal(undefined),
		enabled: signal(true),
		visible: signal(true),
		text: signal('Close'),
		linkType: 'no-action',
		willCloseDialog: true,
	}];

	public volunteerDetails = computed<VolunteerDetails | undefined>(() => {
		return this.dialogDataService.volunteerDetails();
	});
}